import React, {useCallback, useEffect, useMemo, useState} from "react";
import {
  CENTER,
  START,
  defaultGapping,
  defaultLayouts,
  defaultPadding,
  defaultTypography,
  golfstatusColors,
  golfstatusSpacing,
  GSActionBar,
  GSSelect,
  GSSidePanelPage,
  GSToggle,
} from "golfstatus_react_components";
import {faChevronLeft, faExclamationCircle} from "@fortawesome/free-solid-svg-icons";
import {useMatch, useNavigate} from "react-router-dom";

//import scss
import "./tournament-round-hole-auto-assigment.scss";
import {useDispatch, useSelector} from "react-redux";
import {
  autoAssignHoles,
  clearRoundNotifications,
  deactivateRoundScorecards,
  getRound,
  saveRound,
  selectCurrentRound,
  selectLoading,
  selectRoundNotifications,
  setCurrentRound,
} from "../../../../reducers/roundSlice";
import {useNotificationBanner} from "../../../../hooks/notificationHooks";
import {getNotificationItemInfo} from "../../../../helpers/Converters";
import { useRoundHooks, isLinkedRound } from "../../../../hooks/round/roundHooks";
import { getName } from "../../../../app/api";

//Name the component
const TournamentRoundHoleAutoAssignment = () => {
  //hooks
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const match = useMatch(
    "/:tournamentID/rounds/:roundID/hole-assignments/auto-assign"
  );

  //constants
  const SortBy = useMemo(() => {
    return Object.freeze({
      TEAM: { label: "Team Handicap", value: "team" },
      PLAYER: { label: "Player Handicap", value: "player" },
    });
  }, []);

  const SortDirection = useMemo(() => {
    return Object.freeze({
      LOW_TO_HIGH: { label: "Low to High", value: "asc" },
      HIGH_TO_LOW: { label: "High to Low", value: "desc" },
    });
  }, []);

  //local state
  const [selectedOption, setSelectedOption] = useState(null);
  const [balanceAssignments, setBalanceAssignments] = useState(true);

  //selectors
  const notifications = useSelector(selectRoundNotifications);
  const currentRound = useSelector(selectCurrentRound);
  const loading = useSelector(selectLoading);

  //teams are spread across the rounds sharing this round's number, so there is
  //only something to balance when it has some
  const {allRounds} = useRoundHooks();
  const isLinked = isLinkedRound(currentRound, allRounds);

  const getAutoAssignSortOptions = useCallback(() => {
    if (!currentRound) {
      return [];
    }
    if (currentRound?.numberOfPlayers > 1) {
      return [
        { label: SortBy.TEAM.label, subLabel: SortDirection.LOW_TO_HIGH.label, value: { sort: SortBy.TEAM.value, direction: SortDirection.LOW_TO_HIGH.value } },
        { label: SortBy.TEAM.label, subLabel: SortDirection.HIGH_TO_LOW.label, value: { sort: SortBy.TEAM.value, direction: SortDirection.HIGH_TO_LOW.value } },
        { label: SortBy.PLAYER.label, subLabel: SortDirection.LOW_TO_HIGH.label, value: { sort: SortBy.PLAYER.value, direction: SortDirection.LOW_TO_HIGH.value } },
        { label: SortBy.PLAYER.label, subLabel: SortDirection.HIGH_TO_LOW.label, value: { sort: SortBy.PLAYER.value, direction: SortDirection.HIGH_TO_LOW.value } },
      ];
    }
    return [
      { label: SortBy.PLAYER.label, subLabel: SortDirection.LOW_TO_HIGH.label, value: { sort: SortBy.PLAYER.value, direction: SortDirection.LOW_TO_HIGH.value } },
      { label: SortBy.PLAYER.label, subLabel: SortDirection.HIGH_TO_LOW.label, value: { sort: SortBy.PLAYER.value, direction: SortDirection.HIGH_TO_LOW.value } },
    ];
  }, [currentRound, SortBy, SortDirection]);

  //effects
  useEffect(() => {
    if (match?.params?.roundID) {
      dispatch(getRound(match.params.roundID));
    }
    return () => {
      dispatch(clearRoundNotifications());
    };
  }, [dispatch, match?.params?.roundID]);

  // Set the default option after we know the number of players
  useEffect(() => {
    setSelectedOption(getAutoAssignSortOptions()[0]);
  }, [currentRound, getAutoAssignSortOptions]);

  //component functionality

  const getAssignedTeamCount = (holeAssignments) => {
    return (
      holeAssignments?.reduce?.(
        (total, ha) =>
          total + (ha?.roundHoleAssignmentTournamentTeams?.length ?? 0),
        0
      ) ?? 0
    );
  };

  const autoAssign = () => {
    let assign = {
      roundID: match?.params?.roundID,
      //the endpoint sorts and places the teams, so it takes the choice rather
      //than a list worked out here
      sortBy: selectedOption?.value?.sort,
      sortDirection: selectedOption?.value?.direction,
      //only a linked round has rounds to balance against
      balanced: isLinked ? balanceAssignments : undefined,
    };

    dispatch(autoAssignHoles(assign)).then((response) => {
      //the banner carries a failure, so there is nothing to follow up on
      if (response?.meta?.requestStatus !== "fulfilled") {
        return;
      }

      //this round is assigned. a linked round then asks about the rest.
      if (isLinked) {
        navigate("assign-all", {
          replace: true,
          state: {
            assignment: assign,
            assignedTeams: getAssignedTeamCount(response?.payload),
          },
        });
        return;
      }

      navigate(-1);
    });
  };

  const getDrawerActions = () => {
    let actions = [
      {
        name: "Assign",
        isDisabled: !selectedOption,
        action: autoAssign,
        type: "black",
      },
      { name: "cancel", action: leftNavigation, type: "grey" },
    ];
    return actions;
  };

  const leftNavigation = () => {
    navigate(-1);
  };

  const getNavigation = () => {
    //Add Title to your component
    return {
      title: `Auto Assign Holes`,
      leftIcon: faChevronLeft,
      leftButtonClick: leftNavigation,
    };
  };

  const getContent = () => {
    return (
      <gs-form>
        <GSActionBar type="form-header" header="Auto Assign Holes"></GSActionBar>
        <div style={{ ...defaultLayouts.stickyTop, backgroundColor: golfstatusColors.white, opacity: 1.0, zIndex: 100 }}>
          <div style={{
            ...defaultLayouts.fullWidth,
            ...defaultLayouts.vStack.align(START, CENTER),
            ...defaultGapping.xxLargeGap,
            ...defaultPadding.xLargePad,
            paddingTop: golfstatusSpacing.xxLargePad,
            paddingBottom: golfstatusSpacing.xxLargePad,
            borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
          }}>
            <div style={{
              ...defaultLayouts.fullWidth,
              ...defaultLayouts.vStack.align(START, CENTER),
              ...defaultGapping.mediumGap,
            }}>
              <div style={{
                ...defaultTypography.overline2,
                ...defaultTypography.uppercase,
                color: golfstatusColors.grey800,
              }}>ASSIGNMENT TYPE *</div>
              <GSSelect
                isClearable
                options={getAutoAssignSortOptions()}
                placeholder="Select an Assignment Type..."
                selectedOption={selectedOption}
                onChange={(option) => {setSelectedOption(option)}}
              ></GSSelect>
            </div>
            {isLinked && (
              <div style={{
                ...defaultLayouts.fullWidth,
                ...defaultLayouts.vStack.align(START, CENTER),
                ...defaultGapping.mediumGap,
              }}>
                <div style={{
                  ...defaultTypography.overline2,
                  ...defaultTypography.uppercase,
                  color: golfstatusColors.grey800,
                }}>BALANCE ASSIGNMENTS</div>
                <div style={{ ...defaultTypography.xSmallBody, opacity: 0.6 }}>
                  Evenly assign players and teams for a balanced spread across rounds.
                </div>
                <GSToggle
                  value={balanceAssignments}
                  trueDescription="Yes"
                  falseDescription="No"
                  onClick={() => {setBalanceAssignments(!balanceAssignments)}}
                ></GSToggle>
              </div>
            )}
          </div>
        </div>
      </gs-form>
    );
  };

  const getDrawer = () => {
    return {
      actions: getDrawerActions(),
    };
  };

  const timeoutAction = () => {
    dispatch(clearRoundNotifications());
  };

  const revertRound = () => {
    let update = {...currentRound}
    update.state = "ready"
    dispatch(setCurrentRound(update))
    dispatch(saveRound(update))
  }

  let notificationSettings = {
    notifications: notifications,
    timeoutAction,
    defaultBanner:
      currentRound.state === "live"
        ? {
            title: getNotificationItemInfo(
              `Round can't be live when auto assigning hole assignments`,
              faExclamationCircle,
              "This round is live."
            ),
            state: "warning",
            bannerActions: [
              {
                title: "Deactivate Round",
                actionClick: revertRound,
              },
            ],
          }
        : 
        currentRound?.hasScorecards ?
        {
          title: getNotificationItemInfo(
            `Adding new scorecards might have undesired effects.`,
            faExclamationCircle,
            "Scorecards have already been generated"
          ),
          state: "warning",
          bannerActions: [
            {
              title: "clear scorecards",
              actionClick: () => {dispatch(deactivateRoundScorecards(match.params.roundID));},
            },
          ],
        } : undefined,
  };

  const [bannerNotifications] = useNotificationBanner(notificationSettings);

  return (
    //name the component tag
    <tournament-round-hole-auto-assigment>
      <GSSidePanelPage
        header={getNavigation()}
        banner={bannerNotifications}
        content={getContent()}
        drawer={getDrawer()}
        //the sort options and the banner come from the round, so the form waits on it
        loading={[autoAssignHoles, getRound].map(getName).some(item => loading?.includes(item))}
        loadingText={loading?.includes(getName(autoAssignHoles)) ? "Auto Assigning Holes..." : "Loading Round..."}
      ></GSSidePanelPage>
    </tournament-round-hole-auto-assigment>
  );
};

//export the new name
export default TournamentRoundHoleAutoAssignment;
